// src\components\Payroll\PayrollSalaryBatchFld\shared\AddEditBatchDetailModal.jsx
import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import { Button, CreateTableNew as CreateTable } from "../../../../common/APICommon";
import { AddEditBatchDetailModal } from "../shared/AddEditBatchDetailModal";

/**
 * BatchDetailSection
 *
 * Inline section (not a tab) — rendered inside the PartialSalaryBatchModal body.
 *
 * Behaviour:
 *   - Add button opens AddEditBatchDetailModal
 *   - Edit button opens AddEditBatchDetailModal (toggle IsActive only or change PartialSalary)
 *   - No delete — IsActive is used instead (same pattern as VesselFunctionDefinitionTab)
 *
 * Table columns: Line #, Employee Type, Position, Amount, Is Active, Actions
 * Rows stored in form.partialSalaryBatchDetails (array).
 */
export const BatchDetailSection = ({ form, set, options, isViewOnly, errors = {}, showGeneralMessage }) => {
  const rows = form.partialSalaryBatchDetails || [];
  const editable = !isViewOnly;

  const [currentPage, setCurrentPage] = useState(1);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);

  const updateRows = (updated) => set("partialSalaryBatchDetails", updated);

  // ── Open Add ──────────────────────────────────────────────────────────────
  const openAdd = () => {
    setModalMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setModalMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  // ── Handlers passed to modal ──────────────────────────────────────────────
  const handleAdd = (rowForm) => {
    const match = (options?.partialSalaries || []).find(
      (s) => String(s.value) === String(rowForm.partialSalaryId)
    );
    updateRows([
      ...rows,
      {
        id: null,
        partialSalaryId: rowForm.partialSalaryId,
        employeeType: match?.employeeType || "",
        position: match?.position || "",
        amount: match?.amount ?? 0,
        isActive: rowForm.isActive,
      },
    ]);
  };

  const handleUpdate = (rowForm, index) => {
    const match = (options?.partialSalaries || []).find(
      (s) => String(s.value) === String(rowForm.partialSalaryId)
    );
    updateRows(
      rows.map((r, i) =>
        i === index
          ? {
              ...r,
              partialSalaryId: rowForm.partialSalaryId,
              employeeType: match?.employeeType || r.employeeType,
              position: match?.position || r.position,
              amount: match?.amount ?? r.amount,
              isActive: rowForm.isActive,
            }
          : r
      )
    );
  };

  // ── Table config ──────────────────────────────────────────────────────────
  const headers = [
    "Line #",
    "Employee Type",
    "Position",
    "Amount",
    "Is Active",
    ...(editable ? ["Actions"] : []),
  ];
  const accessors = [
    "lineNumber",
    "employeeType",
    "position",
    "amount",
    "isActiveDisplay",
    ...(editable ? ["actions"] : []),
  ];

  const tableData = rows.map((row, index) => ({
    ...row,
    lineNumber: index + 1,
    _index: index,
    isActiveDisplay: row.isActive
      ? (
        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
          Active
        </span>
      )
      : (
        <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
          Inactive
        </span>
      ),
  }));

  const actions = editable
    ? [
        {
          name: "Edit",
          icon: <EditIcon fontSize="small" />,
          className: "bg-green-500 hover:bg-green-600 flex items-center",
          function: (row) => openEdit(row),
          visible: true,
        },
      ]
    : [];

  return (
    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-gray-500">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
          Batch Details
        </span>
        {editable && (
          <Button
            icon={<AddIcon />}
            name="Add Row"
            className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"
            onClick={openAdd}
          />
        )}
      </div>

      <CreateTable
        tableName="BatchDetailTable"
        headers={headers}
        accessors={accessors}
        data={tableData}
        actions={actions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={tableData.length}
      />

      <AddEditBatchDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        selectedData={selectedRow}
        options={options}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        existingRows={rows}
        showGeneralMessage={showGeneralMessage}
      />
    </div>
  );
};